import moment from 'moment'

/**
 * Pure helpers behind the filter pills and saved views of `DjangoList`.
 *
 * A list "state" is what is remembered per list between visits and, but
 * for the text search (which applies to whichever view is selected), what
 * a saved view stores:
 *
 *   { filters: [{ field, value, labels? }], textFilter, order, columns }
 *
 * `field` is the name of a filter described by the backend list response
 * (`filters`, see `utilities/list_filters.py` in preven-web-backend), and
 * the shape of `value` depends on its type:
 *
 * - choice / foreign_key: array of selected values (`labels` keeps the
 *   display label of each foreign key value, so pills don't need to
 *   fetch them back);
 * - boolean: true / false;
 * - date / datetime / number: `{ from, to }` (either may be empty);
 * - text: the searched string.
 *
 * `isDefault` marks the filters that come from the list's `defaultFilters`
 * prop and haven't been edited (see `appliedFilters`).
 */

export const EMPTY_STATE = {
  filters: [],
  textFilter: '',
  order: null,
  columns: null,
}

const isBlank = (value) => value === undefined || value === null || value === ''

export const isFilterValueEmpty = (value) => {
  if (Array.isArray(value)) return value.length === 0
  if (value && typeof value === 'object')
    return isBlank(value.from) && isBlank(value.to)
  return isBlank(value)
}

export const normalizeState = (state) => ({
  filters: ((state && state.filters) || []).filter(
    (filter) => filter && filter.field && !isFilterValueEmpty(filter.value)
  ),
  textFilter: (state && state.textFilter) || '',
  order: (state && state.order) || null,
  columns: (state && state.columns) || null,
})

/** Keys that identify a state, ignoring display-only data (labels). */
const comparable = (state) => {
  const { filters, textFilter, order, columns } = normalizeState(state)
  return JSON.stringify({
    filters: filters.map(({ field, value }) => ({ field, value })),
    textFilter: textFilter.trim(),
    order,
    columns,
  })
}

export const statesEqual = (a, b) => comparable(a) === comparable(b)

/** What a saved view stores: everything but the text search. */
export const viewState = (state) => normalizeState({ ...state, textFilter: '' })

/**
 * State of the list once `view` is selected (null is the built-in "Padrão"
 * view, `defaultState`). The current text search keeps applying, since it
 * belongs to the list rather than to a view, and "Padrão" keeps the shown
 * columns, a personal preference.
 */
export const stateForView = (view, defaultState, current) => ({
  ...(view
    ? normalizeState(view.state)
    : { ...defaultState, columns: normalizeState(current).columns }),
  textFilter: normalizeState(current).textFilter,
})

/**
 * Builds the state of the built-in "Padrão" view out of the `defaultFilters`
 * and `defaultSort` props of a list.
 */
export const defaultListState = (defaultFilters = {}, defaultSort = null) =>
  normalizeState({
    filters: Object.entries(defaultFilters || {}).map(([field, value]) => ({
      field,
      value:
        Array.isArray(value) || typeof value === 'object' ? value : [value],
      isDefault: true,
    })),
    order: defaultSort,
  })

const toList = (value) => (Array.isArray(value) ? value : [value])

/**
 * Filters that apply to the request. The list's own default filters
 * (`isDefault`, until the user edits them) step aside during a text
 * search, as they always did: searching an order number finds it whatever
 * its status.
 */
export const appliedFilters = (filters = [], textFilter = '') =>
  (textFilter || '').trim()
    ? filters.filter((filter) => !filter.isDefault)
    : filters

/** Query params understood by `BaseModelListAPI.read` for the filters. */
export const filtersToParams = (filters = [], definitions = {}) => {
  const params = {}
  filters.forEach(({ field, value }) => {
    if (isFilterValueEmpty(value)) return
    const type = definitions[field]?.type

    switch (type) {
      case 'boolean':
        params[field] = value ? 'True' : 'False'
        break
      case 'text':
        params[`${field}__icontains`] = String(value).trim()
        break
      case 'date':
      case 'number':
      case 'datetime': {
        const lookup = type === 'datetime' ? `${field}__date` : field
        if (!isBlank(value.from)) params[`${lookup}__gte`] = value.from
        if (!isBlank(value.to)) params[`${lookup}__lte`] = value.to
        break
      }
      default:
        // choice, foreign_key and filters not (yet) described by the
        // backend: repeated params, `__in` when more than one value
        params[field] = toList(value)
    }
  })
  return params
}

const capitalize = (text = '') =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text

const formatDate = (value) =>
  moment(value, 'YYYY-MM-DD', true).isValid()
    ? moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY')
    : value

const formatRange = ({ from, to }, format) => {
  if (!isBlank(from) && !isBlank(to)) return `${format(from)} → ${format(to)}`
  if (!isBlank(from)) return `a partir de ${format(from)}`
  return `até ${format(to)}`
}

export const filterLabel = (filter, definition) =>
  capitalize(
    definition?.label || filter.field.replace(/__/g, ' ').replace(/_/g, ' ')
  )

export const filterValueText = (filter, definition) => {
  const { value, labels = {} } = filter
  const type = definition?.type

  if (type === 'boolean') return value ? 'Sim' : 'Não'
  if (type === 'text') return `contém "${value}"`
  if (type === 'date' || type === 'datetime')
    return formatRange(value, formatDate)
  if (type === 'number') return formatRange(value, (v) => v)

  const names = toList(value).map((item) => {
    const choice = (definition?.choices || []).find(
      (option) => String(option.value) === String(item)
    )
    return capitalize(String(choice?.label ?? labels[item] ?? item))
  })
  return names.length > 2
    ? `${names.slice(0, 2).join(', ')} +${names.length - 2}`
    : names.join(', ')
}

export const pillText = (filter, definition) =>
  `${filterLabel(filter, definition)}: ${filterValueText(filter, definition)}`

/** Initial (empty) value for a newly added filter of the given type. */
export const emptyFilterValue = (type) => {
  if (type === 'boolean') return true
  if (type === 'text') return ''
  if (['date', 'datetime', 'number'].includes(type)) return { from: '', to: '' }
  return []
}

/* Remembered state, per list and tenant, in the browser */

const storageKey = (djangoApp, djangoModel) =>
  `listState/${
    localStorage.getItem('databaseId') || ''
  }/${djangoApp}/${djangoModel}`

export const readRememberedState = (djangoApp, djangoModel) => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(storageKey(djangoApp, djangoModel))
    )
    if (!stored || !stored.state) return null
    return {
      activeViewId: stored.activeViewId ?? null,
      state: normalizeState(stored.state),
    }
  } catch (e) {
    return null
  }
}

export const rememberState = (djangoApp, djangoModel, activeViewId, state) => {
  try {
    localStorage.setItem(
      storageKey(djangoApp, djangoModel),
      JSON.stringify({ activeViewId, state: normalizeState(state) })
    )
  } catch (e) {
    // Storage full or blocked: the list keeps working, just not remembered
  }
}
